import React from 'react';
import { Helmet } from 'react-helmet-async';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { matchPathname } from '../util/routes';

const MAPBOX_SCRIPT_ID = 'mapbox_GL_JS';
const GOOGLE_MAPS_SCRIPT_ID = 'GoogleMapsApi';

/**
 * Map library is shown on some of the pages, but ReusableMapContainer is used app wide.
 * However, we can defer the map library loading on pages that don't show the map immediately.
 * Note: this currently only affects Mapbox library.
 * Google Maps library is always loaded immediately. (It seems to be more fragile when loaded asynchronously.)
 *
 * @param {string} initialPathname - The initial pathname at the time of the full page load.
 * @param {array} routeConfiguration - The route configuration.
 * @returns {boolean} - True if the map library can be deferred, false otherwise.
 */
const canDeferMapLibrary = (initialPathname, routeConfiguration) => {
  if (!initialPathname) {
    return false;
  }
  const matchedRoutes = matchPathname(initialPathname, routeConfiguration);
  const currentRouteConfig = matchedRoutes.length > 0 ? matchedRoutes[0]?.route : null;
  return currentRouteConfig?.prioritizeMapLibraryLoading !== true;
};

/**
 * Include scripts (like Map Provider).
 * These scripts are relevant for whole application: location search in Topbar and maps on different pages.
 * However, if you don't need location search and maps, you can just omit this component from app.js
 * Note: another common point to add <scripts>, <links> and <meta> tags is Page.js
 *       and Stripe script is added in public/index.html
 *
 * Note 2: When adding new external scripts/styles/fonts/etc.,
 *         if a Content Security Policy (CSP) is turned on, the new URLs
 *         should be whitelisted in the policy. Check: server/csp.js
 */
export const IncludeScripts = props => {
  const { marketplaceRootURL: rootURL, maps, analytics } = props?.config || {};
  const { googleAnalyticsId, plausibleDomains } = analytics;

  const routeConfiguration = useRouteConfiguration();
  // Note: Affects Mapbox only. Google Maps initialization is not yet ready to support asynchronous loading.
  const deferMapLibrary = canDeferMapLibrary(props?.initialPathname, routeConfiguration)
    ? { defer: '' }
    : {};

  const { mapProvider, googleMapsAPIKey, mapboxAccessToken } = maps || {};
  const isGoogleMapsInUse = mapProvider === 'googleMaps';
  const isMapboxInUse = mapProvider === 'mapbox';

  // Add Google Analytics script if correct id exists (it should start with 'G-' prefix)
  // See: https://developers.google.com/analytics/devguides/collection/gtagjs
  const hasGoogleAnalyticsv4Id = googleAnalyticsId?.indexOf('G-') === 0;

  // Collect relevant map libraries
  let mapLibraries = [];
  let analyticsLibraries = [];
  // KLARO-FASE-1: librerías del CMP de consentimiento (Klaro).
  let consentLibraries = [];

  if (isMapboxInUse) {
    // NOTE: remember to update mapbox-sdk.min.js to a new version regularly.
    // mapbox-sdk.min.js is included from static folder for CSP purposes.
    mapLibraries.push(
      <script
        key="mapboxSDK"
        src={`${rootURL}/static/scripts/mapbox/mapbox-sdk@0.16.2/mapbox-sdk.min.js`}
        async
      ></script>
    );
    // License information for v3.7.0 of the mapbox-gl-js library:
    // https://github.com/mapbox/mapbox-gl-js/blob/v3.7.0/LICENSE.txt

    // Add CSS for Mapbox map
    mapLibraries.push(
      <link
        key="mapbox_GL_CSS"
        href="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.css"
        rel="stylesheet"
        crossOrigin="anonymous"
      />
    );
    // Add Mapbox library
    mapLibraries.push(
      <script
        id={MAPBOX_SCRIPT_ID}
        key="mapbox_GL_JS"
        src="https://api.mapbox.com/mapbox-gl-js/v3.7.0/mapbox-gl.js"
        crossOrigin="anonymous"
        {...deferMapLibrary}
      ></script>
    );
  } else if (isGoogleMapsInUse) {
    // Add Google Maps library
    mapLibraries.push(
      <script
        id={GOOGLE_MAPS_SCRIPT_ID}
        key="GoogleMapsApi"
        src={`https://maps.googleapis.com/maps/api/js?key=${googleMapsAPIKey}&libraries=places`}
        crossOrigin="anonymous"
      ></script>
    );
  }

  // KLARO-FASE-2 / CONSENTIMIENTO: cargador por entorno NEUTRALIZADO.
  //
  // En Uroborox la carga de Google Analytics la gobierna EXCLUSIVAMENTE Klaro,
  // a través de los <script type="text/plain" data-name="google-analytics"> de
  // public/index.html y de public/static/scripts/klaro/ga-init.js, que solo se
  // ejecutan cuando el usuario consiente la categoría "analytics".
  //
  // Este bloque hacía lo contrario: en cuanto existía googleAnalyticsId (por el
  // asset `analytics` de Console o por REACT_APP_GOOGLE_ANALYTICS_ID) inyectaba
  // gtag.js y ejecutaba gtag('config', ...) SIN pasar por Klaro. Eso provocaba
  // dos cosas a la vez: GA se cargaba aunque el usuario hubiera rechazado las
  // analíticas (problema de RGPD), y quien aceptaba acababa con doble carga y
  // doble `config` sobre el mismo Measurement ID.
  //
  // Se deja el código, no se borra: es el cargador original del template y sirve
  // de referencia. La constante lo mantiene apagado de forma permanente.
  //
  // OJO: REACT_APP_GOOGLE_ANALYTICS_ID sigue siendo necesaria — es lo que hace
  // que src/index.js instancie el GoogleAnalyticsHandler y se envíen los
  // page_view de navegación interna. Lo que no debe hacer es cargar gtag.js.
  const LOAD_GA_FROM_ENV = false;

  if (LOAD_GA_FROM_ENV && googleAnalyticsId && hasGoogleAnalyticsv4Id) {
    // Google Analytics: gtag.js
    // NOTE: This template is a single-page application (SPA).
    //       gtag.js sends initial page_view event after page load.
    //       but we need to handle subsequent events for in-app navigation.
    //       This is done in src/analytics/handlers.js
    analyticsLibraries.push(
      <script
        key="gtag.js"
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
        crossOrigin="anonymous"
      ></script>
    );

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      // Ensure that gtag function is found from window scope
      window.gtag = function gtag() {
        dataLayer.push(arguments);
      };
      gtag('js', new Date());
      gtag('config', googleAnalyticsId, {
        cookie_flags: 'SameSite=None;Secure',
      });
    }
  }

  if (plausibleDomains) {
    // If plausibleDomains is not an empty string, include their script too.
    analyticsLibraries.push(
      <script
        key="plausible"
        defer
        src="https://plausible.io/js/script.js"
        data-domain={plausibleDomains}
        crossOrigin="anonymous"
      ></script>
    );
  }

  // KLARO-FASE-1: CMP de consentimiento de cookies.
  // - klaro.min.css y klaro.min.js se sirven desde cdn.kiprotect.com
  //   (whitelisted en server/csp.js → customDirectives).
  // - La config (window.klaroConfig) se carga como recurso ESTÁTICO desde
  //   /static (mismo origen, cubierto por 'self'), NO inline, para no chocar
  //   con el CSP en modo `block`. El script de config va SIN defer para que
  //   window.klaroConfig exista antes de que klaro.min.js (defer) lo lea vía
  //   data-config. Klaro se auto-inicializa y muestra el banner al cargar.
  // NOTA: en esta fase NO se conecta Google Analytics (eso es la Fase 2);
  //       el servicio 'google-analytics' está declarado como placeholder en
  //       la config y no carga nada real.
  const KLARO_VERSION = 'v0.7.11';
  consentLibraries.push(
    <link
      key="klaro.css"
      rel="stylesheet"
      href={`https://cdn.kiprotect.com/klaro/${KLARO_VERSION}/klaro.min.css`}
      crossOrigin="anonymous"
    />
  );
  // KLARO-THEME: hoja de tema propia (identidad Uroborox). DESPUÉS de
  // klaro.min.css para ganar en la cascada. Servida desde /static (mismo
  // origen, cubierto por 'self'); no requiere cambios de CSP.
  consentLibraries.push(
    <link
      key="klaro-theme"
      rel="stylesheet"
      href={`${rootURL}/static/scripts/klaro/klaro-theme.css`}
      crossOrigin="anonymous"
    />
  );
  consentLibraries.push(
    <script
      key="klaro-config"
      src={`${rootURL}/static/scripts/klaro/klaro-config.js`}
      crossOrigin="anonymous"
    ></script>
  );
  consentLibraries.push(
    <script
      key="klaro.js"
      defer
      data-config="klaroConfig"
      src={`https://cdn.kiprotect.com/klaro/${KLARO_VERSION}/klaro.js`}
      crossOrigin="anonymous"
    ></script>
  );
  // KLARO-FASE-2: los <script> de GA (gobernados por consentimiento vía Klaro) se
  // declaran en public/index.html, NO aquí: react-helmet descarta los <script>
  // sin `src`/`innerHTML`, así que un <script type="text/plain" data-src> se
  // perdía. Ver public/index.html (data-name="google-analytics") y ga-init.js.

  const isBrowser = typeof window !== 'undefined';
  const isMapboxLoaded = isBrowser && window.mapboxgl;

  // If Mapbox is loaded, we can set the accessToken already here.
  // This is the execution flow with the production build,
  // since SSR includes those map libraries to <head> of the app.
  if (isMapboxInUse && isMapboxLoaded && !window.mapboxgl.accessToken) {
    // Add access token for Mapbox library
    window.mapboxgl.accessToken = mapboxAccessToken;
  }

  // If the script is added on client side as a reaction to page navigation or
  // the app is rendered on client side entirely (e.g. HMR/WebpackDevServer),
  // we need to listen when the script is loaded.
  const onMapLibLoaded = () => {
    // At this point we know that map library is loaded after it's dynamically included
    if (isMapboxInUse && !window.mapboxgl.accessToken) {
      // Add access token for Mapbox sdk.
      window.mapboxgl.accessToken = mapboxAccessToken;
    }
  };

  // React Helmet Async doesn't support onLoad prop for scripts.
  // However, it does have onChangeClientState functionality.
  // We can use that to start listen 'load' events when the library is added on client-side.
  const onChangeClientState = (newState, addedTags) => {
    if (addedTags && addedTags.scriptTags) {
      const foundScript = addedTags.scriptTags.find(s =>
        [MAPBOX_SCRIPT_ID, GOOGLE_MAPS_SCRIPT_ID].includes(s.id)
      );
      if (foundScript) {
        foundScript.addEventListener('load', onMapLibLoaded, { once: true });
      }
    }
  };

  // KLARO-FASE-1: el CMP se incluye primero para que el banner pueda
  // aparecer cuanto antes en la carga.
  const allScripts = [...consentLibraries, ...analyticsLibraries, ...mapLibraries];
  return <Helmet onChangeClientState={onChangeClientState}>{allScripts}</Helmet>;
};
